import type { BusinessAddressRef } from '@seek/hirer-business-address';
import { useLanguage } from '@vocab/react';
import { Box, ContentBlock, Stack, Alert } from 'braid-design-system';
import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router';

import { UserPermissionsAlert } from 'src/app/shared/UserPermissionsAlert';
import { ROUTE_MANAGE } from 'src/app/shared/constants/routes';
import { NAVIGATION_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import { saveTrackingData } from 'src/app/shared/dataAnalytics/storage';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { convertToCurrency } from 'src/app/utils/currency';
import { getIsBusinessAddressVerified } from 'src/app/utils/getIsBusinessAddressVerified';
import { log } from 'src/app/utils/logging';
import { UnexpectedError } from 'src/app/v2/components/UnexpectedError';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useContractBalance } from 'src/app/v2/hooks/useContractBalance';
import { useCurrentAdvertiserContext } from 'src/app/v2/hooks/useCurrentAdvertiserContext';
import { useDraft } from 'src/app/v2/hooks/useDraft';
import { useGetOrderSummary } from 'src/app/v2/hooks/useGetOrderSummary';
import { useHirerAccount } from 'src/app/v2/hooks/useHirerAccount';
import { useQueryParam } from 'src/app/v2/hooks/useQueryParam';
import { PayAndPostErrorPageLayout } from 'src/app/v2/layouts/error';
import { generateIdempotencyKey } from 'src/app/v2/utils/generateJobIdempotencyKey';
import {
  getShouldShowPremiumAsiaUpsell,
  getShouldShowPremiumUpsell,
} from 'src/app/v2/utils/getShouldShowPremiumUpsell';
import {
  BillingStatus,
  type CreateJobMutation,
  useCreateJobMutation,
  UserIntentType,
} from 'src/graphql/generated';

import { AdbudgetUpsellSection } from './sections/adbudget';
import { BusinessAddressSection } from './sections/businessAddress';
import { CheckoutSection } from './sections/checkout';
import { CreditHoldSection } from './sections/creditHold';
import { GenericError } from './sections/error';
import { FooterSection } from './sections/footer';
import { HeaderSection } from './sections/header';
import { PremiumUpsellSection } from './sections/premiumUpsell';
import { PremiumAsiaUpsellSection } from './sections/premiumUpsellAsia';
import { PreviewSection } from './sections/preview';

/**
 * REMINDER: Atomic design
 * This is a page amd is divided into smaller sections (organisms)
 * Refer to /sections for components next level down
 * https://medium.com/galaxy-ux-studio/principles-of-atomic-design-7b03a30c3cb6
 */
export const PayAndPostPageV2 = () => {
  const { trackingAdapter } = useAnalyticsTracking();
  const draftId = useQueryParam('draftid');
  const hirerAccount = useHirerAccount();
  const contractBalance = useContractBalance();
  const currentAdvertiserContext = useCurrentAdvertiserContext();
  const draft = useDraft();
  const {
    assignJobIdAndRevisionToParentProduct,
    cartState,
    setCartId,
    setInputData,
    orderState: { order: { orderId, orderNumber } = {} },
  } = useShoppingCart();
  const { locale } = useLanguage();
  const shouldShowBusinessAddressForm = useRef(
    !getIsBusinessAddressVerified(hirerAccount?.postalAddress),
  );
  const isOnCreditHold =
    hirerAccount?.billingStatus === BillingStatus.ON_CREDIT_HOLD;
  const isAllowedToCreateJobs =
    currentAdvertiserContext?.permissionOptions.canCreateJobs;
  const businessAddressRef = useRef<BusinessAddressRef>(null);
  const jobIdempotencyKey = generateIdempotencyKey(draft!);
  const orderSummary = useGetOrderSummary(orderId, hirerAccount?.nation);
  const navigate = useNavigate();

  const paymentsReturnValue = useQueryParam('payments_return');
  const isReturnedFrom3rdPayment = Boolean(paymentsReturnValue);

  const [createJob, { data: job, error: jobCreationError }] =
    useCreateJobMutation({
      variables: {
        input: {
          draftId: draftId!,
          idempotencyKey: jobIdempotencyKey,
        },
      },
    });
  const [checkoutError, setCheckoutError] = useState<Error | null>(null);

  useEffect(() => {
    trackingAdapter.sendPageEvent();
  }, [trackingAdapter]);

  useEffect(() => {
    if (draftId) {
      setCartId(draftId);
    }
  }, [draftId, setCartId]);

  useEffect(() => {
    if (!isReturnedFrom3rdPayment && draft) {
      setInputData({
        draftId: draft.id,
        locale,
        locationId: draft.locationsV2?.[0].id || '',
        opportunityId: draft.id,
        roleTitle: draft.title || '',
        subClassificationId: draft.categories?.[0].subCategoryId || '',
        userIntent: UserIntentType.PURCHASE,
      });
    }
  }, [setInputData, isReturnedFrom3rdPayment, draft, locale]);

  useEffect(() => {
    const shouldCreateJob = !isReturnedFrom3rdPayment;

    if (shouldCreateJob) {
      createJob().then((response) => {
        if (response.data?.createJob) {
          assignJobIdAndRevisionToParentProduct({
            jobId: response.data?.createJob.jobId,
            revisionId: response.data?.createJob.revisionId,
          });
        }
      });
    }
  }, [
    createJob,
    assignJobIdAndRevisionToParentProduct,
    isReturnedFrom3rdPayment,
  ]);

  useEffect(() => {
    if (orderId) {
      const orderSummaryValue = orderSummary?.total?.value ?? 0;

      const isHirerTypeContract = Boolean(contractBalance);
      const hasSufficientBudget = Boolean(
        isHirerTypeContract && orderSummaryValue === 0,
      );
      const isPayment = Boolean(orderSummaryValue > 0);
      const productTotalPrice = convertToCurrency(orderSummaryValue).toString();

      saveTrackingData({
        hasSufficientBudget,
        isPayment,
        orderNumber,
        productTotalPrice,
      });
    }
  }, [orderSummary, contractBalance, orderId, orderNumber]);

  const onErrorBoundaryTriggered = (error: Error) => {
    log.error(
      'PayAndPostPageV2View | ErrorBoundary | error',
      {
        error,
        draftId,
        jobId: job?.createJob?.jobId,
      },
      true,
    );
  };

  const fullyDiscountedPricingGroup =
    hirerAccount?.pricingGroupContractExists && orderSummary?.total.value === 0;

  const onBackButtonClicked = () => {
    trackingAdapter.sendLinkEvent({
      eventName: NAVIGATION_PRESSED,
      objectInteraction: 'ecaja-checkout-back-interacted',
    });

    navigate(ROUTE_MANAGE);
  };

  return (
    <PayAndPostPageV2View
      error={jobCreationError}
      businessAddressRef={businessAddressRef}
      jobResponse={job?.createJob}
      jobIdempotencyKey={jobIdempotencyKey}
      onError={onErrorBoundaryTriggered}
      shouldRenderAdbudgetUpsell={
        !hirerAccount?.hidePrice &&
        Boolean(hirerAccount?.id) &&
        !Boolean(cartState?.promoCode)
      }
      shouldRenderBusinessAddress={shouldShowBusinessAddressForm.current}
      shouldRenderPremiumUpsell={
        getShouldShowPremiumUpsell({
          cart: cartState?.items,
        }) &&
        Boolean(job?.createJob) &&
        !fullyDiscountedPricingGroup
      }
      shouldRenderPremiumAsiaUpsell={
        getShouldShowPremiumAsiaUpsell({
          cart: cartState?.items,
        }) &&
        Boolean(job?.createJob) &&
        !fullyDiscountedPricingGroup
      }
      shouldRenderCheckout={Boolean(cartState?.id)}
      shouldRenderCheckoutError={Boolean(checkoutError)}
      shouldRenderCreditHoldMessage={isOnCreditHold}
      shouldRenderUserPermissionError={!isAllowedToCreateJobs}
      setCheckoutError={(error) => setCheckoutError(error)}
      onBackButtonClicked={onBackButtonClicked}
    />
  );
};

interface PayAndPostPageV2ViewProps {
  error?: Error;
  businessAddressRef?: React.RefObject<BusinessAddressRef>;
  jobResponse?: CreateJobMutation['createJob'];
  jobIdempotencyKey: string;
  shouldRenderAdbudgetUpsell?: boolean;
  shouldRenderBusinessAddress?: boolean;
  shouldRenderCheckout?: boolean;
  shouldRenderCheckoutError?: boolean;
  shouldRenderCreditHoldMessage?: boolean;
  shouldRenderPremiumUpsell?: boolean;
  shouldRenderPremiumAsiaUpsell?: boolean;
  shouldRenderUserPermissionError?: boolean;
  onError?: (error: Error) => void;
  setCheckoutError: (error: Error | null) => void;
  onBackButtonClicked: () => void;
}

export const PayAndPostPageV2View: React.FC<PayAndPostPageV2ViewProps> = ({
  error,
  businessAddressRef,
  jobResponse,
  jobIdempotencyKey,
  shouldRenderAdbudgetUpsell,
  shouldRenderBusinessAddress,
  shouldRenderPremiumUpsell,
  shouldRenderPremiumAsiaUpsell,
  shouldRenderCreditHoldMessage,
  shouldRenderCheckout,
  shouldRenderCheckoutError,
  shouldRenderUserPermissionError,
  setCheckoutError,
  onError,
  onBackButtonClicked,
}) => {
  if (shouldRenderCreditHoldMessage) {
    return (
      <PayAndPostErrorPageLayout onBackButtonClicked={onBackButtonClicked}>
        <HeaderSection />
        <CreditHoldSection />
      </PayAndPostErrorPageLayout>
    );
  }

  if (shouldRenderUserPermissionError) {
    return (
      <PayAndPostErrorPageLayout onBackButtonClicked={onBackButtonClicked}>
        <HeaderSection />
        <UserPermissionsAlert />
      </PayAndPostErrorPageLayout>
    );
  }

  if (error) {
    return <GenericError onBackButtonClicked={onBackButtonClicked} />;
  }

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <GenericError onBackButtonClicked={onBackButtonClicked} />
      )}
      onError={onError}
    >
      <ContentBlock>
        <Box marginBottom="xxlarge" marginTop="large">
          <Stack space="xxlarge">
            <Stack space="xlarge">
              <HeaderSection />
              {shouldRenderPremiumUpsell && (
                <PremiumUpsellSection job={jobResponse!} />
              )}
              {shouldRenderPremiumAsiaUpsell && (
                <PremiumAsiaUpsellSection job={jobResponse!} />
              )}
              {shouldRenderBusinessAddress && (
                <BusinessAddressSection
                  businessAddressRef={businessAddressRef}
                  onUpdateFailed={setCheckoutError}
                />
              )}
              {shouldRenderAdbudgetUpsell && <AdbudgetUpsellSection />}
              {shouldRenderCheckout && (
                <CheckoutSection
                  jobId={jobResponse?.jobId}
                  jobIdempotencyKey={jobIdempotencyKey}
                  businessAddressRef={businessAddressRef}
                  onCheckoutFailed={setCheckoutError}
                  onBackButtonClicked={onBackButtonClicked}
                  setCheckoutError={setCheckoutError}
                >
                  <PreviewSection />
                </CheckoutSection>
              )}
              {shouldRenderCheckoutError && (
                <Box marginY="medium">
                  <Alert tone="critical">
                    <UnexpectedError />
                  </Alert>
                </Box>
              )}
            </Stack>
            <FooterSection />
          </Stack>
        </Box>
      </ContentBlock>
    </ErrorBoundary>
  );
};
